import type { $Fetch } from 'ofetch';
import { ofetch } from 'ofetch';
import type { AuthGateway } from '@/application/port/out/AuthGateway';
import type {
  ForgotPasswordCommand,
  LoginCommand,
  ResetPasswordCommand,
} from '@/application/port/in/dto/LoginCommand';
import type { AuthSession, User } from '@/domain/model/User';
import { DomainError } from '@/domain/error/DomainError';

interface ApiAuthResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
    accessExpiresInSec: number;
    user: {
      id: string;
      email: string;
      nome: string;
      tenantId: string;
      role: string;
      roles: string[];
      authorities: string[];
    };
  };
}

/**
 * Implementação ofetch do AuthGateway. Endpoints em /api/v1/auth/* — anônimos
 * (sem Authorization). Refresh e logout idem; 401 nesses endpoints não tenta
 * refresh recursivo.
 */
export class HttpAuthGateway implements AuthGateway {
  constructor(
    private readonly baseURL: string,
    /** Client autenticado pra endpoints futuros — login/forgot/reset NÃO usam. */
    private readonly _authedClient: $Fetch,
  ) {
    void this._authedClient; // reservado pra endpoints futuros (ex: /auth/me)
  }

  async login(command: LoginCommand): Promise<AuthSession> {
    const body: Record<string, unknown> = {
      tenantSlug: command.tenantSlug,
      email: command.email,
      password: command.password,
    };
    if (command.totpCode !== undefined) {
      body['totpCode'] = command.totpCode;
    }

    const res = await this.anonPost<ApiAuthResponse>('/api/v1/auth/login', body);
    return this.toSession(res);
  }

  async logout(refreshToken: string): Promise<void> {
    await this.anonPost('/api/v1/auth/logout', null, {
      'X-Refresh-Token': refreshToken,
    });
  }

  async refresh(refreshToken: string): Promise<AuthSession> {
    const res = await this.anonPost<ApiAuthResponse>('/api/v1/auth/refresh', {
      refreshToken,
    });
    return this.toSession(res);
  }

  async forgotPassword(command: ForgotPasswordCommand): Promise<void> {
    await this.anonPost('/api/v1/auth/forgot-password', { tenantSlug: command.tenantSlug, email: command.email });
  }

  async resetPassword(command: ResetPasswordCommand): Promise<void> {
    await this.anonPost('/api/v1/auth/reset-password', {
      token: command.token,
      newPassword: command.newPassword,
    });
  }

  private async anonPost<T>(
    path: string,
    body: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    try {
      return await ofetch<T>(path, {
        method: 'POST',
        baseURL: this.baseURL,
        ...(body !== null ? { body } : {}),
        ...(extraHeaders ? { headers: extraHeaders } : {}),
      });
    } catch (err) {
      const e = err as { data?: unknown; status?: number; statusCode?: number };
      const payload = e.data;
      const status = e.status ?? e.statusCode ?? 500;
      throw DomainError.fromApiError(payload, status);
    }
  }

  private toSession(api: ApiAuthResponse): AuthSession {
    const { data } = api;
    const expiresAt = Date.now() + data.accessExpiresInSec * 1000;
    const user: User = {
      id: data.user.id,
      email: data.user.email,
      nome: data.user.nome,
      tenantId: data.user.tenantId,
      role: data.user.role,
      roles: data.user.roles,
      authorities: data.user.authorities,
    };
    return {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      accessExpiresAt: expiresAt,
      user,
    };
  }
}
